import { useParams } from "react-router-dom";
import { useGetOrganization, useGetProfile } from "../../../../../services";
import type { Lead as LegacyLead } from "../../../../../types";
import { MemberItem } from "./MemberItem";
import {
  BulletSectionTitle,
  Button,
  IconButton,
  Image,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../../components";
import { LeadRowCardSection } from "../card/LeadRowCardSection";
import { homeState } from "../../../../../state/home";
import { getImageUrl } from "../../../../../utils/image";
import { type Lead, ProfileVersion, UserProfile } from "../../../../../client";
import { faClose, faUser } from "@fortawesome/free-solid-svg-icons";
import { useAnimate } from "../../../../../hooks";
import { LeadRowCard } from "../card/LeadRowCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "sonner";
import { useGetMatch } from "../../../../../services/organtization/get-match";
import { useMemo } from "react";
import { iconForName } from "../../../../../components/TechIcons";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { cn } from "../../../../../components/utils/cn";
import SkillsIcon from "../../../../../components/atoms/SkillsIcon";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import {
  getLeadSkillNames,
  getLeadSkillTags,
  getLeadIndustry,
  hasAssignmentData,
} from "../../../../../utils/get-lead-skills";

interface Props {
  profileId?: string;
  profileVersions: ProfileVersion[];
  onMutateLead: (lead: Partial<LegacyLead>) => void;
  profileLinkId?: string;
  leadId?: string;
  lead?: Lead;
  profileCanBeReplaced: boolean;
}

export const MemberPanel = ({
  profileId,
  profileVersions,
  onMutateLead,
  profileLinkId,
  leadId,
  lead,
  profileCanBeReplaced,
}: Props) => {
  const { organizationId } = useParams();
  if (!organizationId) throw new Error("Organization ID is required");
  const [parent] = useAnimate();
  const { data: profileData } = useGetProfile({
    organizationId,
    profileId: profileId ?? "",
  });

  const { data, error } = useGetOrganization(organizationId);

  let link = `${window.location.origin}/p/`;

  const leadSkillNames = getLeadSkillNames(lead);
  const { data: skillMatchData, error: skillMatchError } = useGetMatch({
    organizationId: organizationId,
    skills: leadSkillNames.length > 0 ? leadSkillNames : ["d", "d"],
    industry: getLeadIndustry(lead),
  });

  const onEdit = (profileId: string) => {
    updateHomeState({
      linkCreatorLeadId: leadId ?? null,
    });
  };

  const updateHomeState = (updates: Partial<typeof homeState>) => {
    Object.assign(homeState, updates);
  };

  const getMatchingSkills = (profileId: string) => {
    if (
      skillMatchData &&
      skillMatchData.some((skillMatch) => skillMatch.profileId === profileId)
    ) {
      // Find the matching skills array for the given profileId
      const matchingSkills =
        skillMatchData.find((skillMatch) => skillMatch.profileId === profileId)
          ?.matchingSkillTags || [];

      // Return the items from 'skills' that match the names in 'matchingSkills'
      if (skills) {
        return skills.filter((skill) =>
          matchingSkills.some(
            (matchingSkill) =>
              matchingSkill.name.toLowerCase() === skill?.name?.toLowerCase(),
          ),
        );
      }
    }
    return [];
  };

  const getAvailableDate = (profileId: string) => {
    if (skillMatchData) {
      return skillMatchData.find(
        (skillMatch) => skillMatch.profileId === profileId,
      )?.availableDate;
    }
  };

  const getHaveContract = (profileId: string) => {
    if (skillMatchData) {
      return (
        skillMatchData.find((skillMatch) => skillMatch.profileId === profileId)
          ?.haveContract ?? false
      );
    }
  };

  const getIndustryMatch = (profileId: string) => {
    if (skillMatchData) {
      return (
        skillMatchData.find((skillMatch) => skillMatch.profileId === profileId)
          ?.industryMatch ?? null
      );
    }
  };

  const skills = useMemo(() => {
    return getLeadSkillTags(lead).filter(Boolean);
  }, [lead?.parsedAssignmentData, lead?.assignmentInfo?.skillTags]);

  if (profileId && !profileData) {
    return <p>j</p>;
  }

  if (error || !data) throw new Error("Error fetching organization data");

  var selectedProfile: UserProfile | null = null;
  if (profileId) {
    selectedProfile = data.profiles.filter((p) => p.profileId === profileId)[0];
  }

  const assignmentInfoValid = () => {
    return hasAssignmentData(lead);
  };

  return (
    <div
      className="flex flex-1 flex-col md:flex-row md:max-h-[450px] gap-6"
      ref={parent}
    >
      <LeadRowCard
        className={cn(
          "overflow-scroll md:max-h-[450px] md:flex",
          selectedProfile && "hidden",
        )}
      >
        <LeadRowCardSection title="Pick Member for Lead">
          {data.profiles &&
            data.profiles
              .filter((p) => p.profileId !== profileId)
              .filter((p) => p.profileData.workplaces != null)
              .filter((p) => p.role !== "default")
              .sort((a, b) => {
                const matchingSkillsA = getMatchingSkills(a.profileId);
                const matchingSkillsB = getMatchingSkills(b.profileId);

                // Primary sort: by number of matching skills (descending)
                const primarySort =
                  matchingSkillsB.length - matchingSkillsA.length;

                // If matching skills are equal, apply secondary sort
                if (primarySort === 0) {
                  // Secondary sort: haveContract = false first
                  if (
                    getHaveContract(a.profileId) !==
                    getHaveContract(b.profileId)
                  ) {
                    return getHaveContract(a.profileId) ? 1 : -1; // false comes before true
                  }

                  // Tertiary sort: if both have contracts, sort by availability date (sooner first)
                  if (
                    getHaveContract(a.profileId) &&
                    getHaveContract(b.profileId)
                  ) {
                    const aAvailableDate = getAvailableDate(a.profileId);
                    const bAvailableDate = getAvailableDate(b.profileId);

                    const dateA = aAvailableDate
                      ? new Date(aAvailableDate)
                      : new Date("9999-12-31");
                    const dateB = bAvailableDate
                      ? new Date(bAvailableDate)
                      : new Date("9999-12-31");

                    // Check if dates are within 6 months
                    const now = new Date();
                    const sixMonthsFromNow = new Date(
                      now.getTime() + 6 * 30 * 24 * 60 * 60 * 1000,
                    );

                    const aWithin6Months = dateA <= sixMonthsFromNow;
                    const bWithin6Months = dateB <= sixMonthsFromNow;

                    // Prioritize those available within 6 months
                    if (aWithin6Months !== bWithin6Months) {
                      return aWithin6Months ? -1 : 1;
                    }

                    // If both are within/outside 6 months, sort by actual date
                    return dateA.getTime() - dateB.getTime();
                  }
                }

                return primarySort;
              })
              .map((profile) => (
                <MemberItem
                  selected={profile.profileId === profileId}
                  onClick={() => {
                    onMutateLead({
                      profileId: profile.profileId,
                    });
                  }}
                  key={profile.profileId}
                  disabled={!(!selectedProfile && profileId !== "")}
                  {...profile}
                  matchingSkills={getMatchingSkills(profile.profileId)}
                  availableDate={getAvailableDate(profile.profileId)}
                  haveContract={getHaveContract(profile.profileId) ?? false}
                  industryMatch={getIndustryMatch(profile.profileId) ?? null}
                />
              ))}
        </LeadRowCardSection>
      </LeadRowCard>

      <LeadRowCard className="flex-1 max-h-[450px]">
        {selectedProfile && profileId ? (
          <div className="h-full min-h-[450px]">
            <div className="text-sm px-4 mb-2 rounded-2xl flex flex-1 flex-row justify-between">
              <BulletSectionTitle>Lead Profile</BulletSectionTitle>
            </div>

            <div className="flex flex-col justify-center items-center relative overflow-visible">
              <div
                className={cn(
                  "flex flex-1 rounded-2xl min-h-60 h-60 flex-col items-center justify-center relative mt-1 p-6",
                  assignmentInfoValid()
                    ? "bg-[linear-gradient(90deg,rgba(57,220,234,0.05)_10%,rgba(255,40,166,0.05)_88%)] min-w-72 w-72"
                    : "bg-gray-1 min-w-80 w-80",
                )}
              >
                <div className="relative">
                  <Image
                    src={
                      selectedProfile.profilePhotoId
                        ? getImageUrl(selectedProfile.profilePhotoId)
                        : undefined
                    }
                    alt={
                      typeof selectedProfile.profileData.name === "string"
                        ? selectedProfile.profileData.name
                        : "Profile"
                    }
                    className="aspect-square w-14 h-14 rounded-2xl md:w-14 md:h-14"
                  />
                </div>
                <h5 className="text-md font-semibold mt-2">
                  {typeof selectedProfile.profileData.name === "string"
                    ? selectedProfile.profileData.name
                    : "Profile"}
                </h5>
                <p className="text-sm whitespace-pre-line text-center">
                  {assignmentInfoValid()
                    ? `This Lead Profile is optimized with`
                    : `This Lead Profile is enriched with:`}
                </p>
                <div className="flex flex-col items-start justify-start w-full mt-4 gap-1">
                  <div className={cn("flex items-center gap-2")}>
                    <FontAwesomeIcon icon={faCircleCheck} size="sm" />
                    <p className="text-sm">Summary</p>
                  </div>
                  <div className={cn("flex items-center gap-2")}>
                    <FontAwesomeIcon icon={faCircleCheck} size="sm" />
                    <p className="text-sm">Highlights</p>
                  </div>
                  <div className={cn("flex items-center gap-2")}>
                    <FontAwesomeIcon icon={faCircleCheck} size="sm" />
                    <p className="text-sm">Top skills</p>
                  </div>

                  {!assignmentInfoValid() && (
                    <div className="w-full mt-2 text-center bg-[linear-gradient(90deg,rgba(57,220,234,0.05)_10%,rgba(255,40,166,0.05)_88%)] p-2 rounded-3xl">
                      <p className="text-sm">Add assignment info to optimize</p>
                    </div>
                  )}
                </div>
                {profileCanBeReplaced === true && (
                  <IconButton
                    className="absolute top-[8px] right-[8px] bg-gray-2 rounded-md z-10"
                    size="small"
                    icon={faClose}
                    iconProps={{
                      className: "text-black", // Pass custom class for the icon
                    }}
                    onClick={() =>
                      onMutateLead({
                        profileId: undefined,
                      })
                    }
                  />
                )}

                <div className="flex flex-1 items-center gap-3 mt-4">
                  <Button
                    variant="primary"
                    onClick={() => {
                      onEdit(profileId);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      window.open(link + `${profileLinkId}`, "_blank");
                    }}
                  >
                    View
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full min-h-[450px] flex flex-1 justify-center items-center">
            <LeadRowCard
              type="dashed"
              className="bg-red-1 text-black text-sm  border-red-2"
            >
              <div className="flex flex-1 items-center gap-3">
                <FontAwesomeIcon
                  width={14}
                  height={14}
                  icon={faUser}
                  className="w-4 h-4 shrink-0 text-red-3"
                />
                <p>
                  <span className="font-bold">Pick a Member</span> to generate a{" "}
                  <br />
                  optimized <span className="font-bold">Lead Profile</span> for
                  this <span className="font-bold">Lead</span>
                </p>
              </div>
            </LeadRowCard>
          </div>
        )}
      </LeadRowCard>
    </div>
  );
};

MemberPanel.displayName = "MemberPanel";
